/**
 * Kids Place Stories — all catalog, storyplay JSON, and media assets are loaded
 * through kidsstories_backend (Laravel). The browser never fetches Firebase Storage directly.
 *
 * Required env: VITE_API_BASE_URL — Laravel API root, e.g. http://kidsstories.local/api
 * Dev shortcut:  /stories-api  (Vite proxy → kidsstories.local, see vite.config.js)
 */

import axios from 'axios';

// Used only server-side in URL params — browser loads assets via /stories/asset proxy.
const STORAGE_BASE_URL = (
  import.meta.env.VITE_STORIES_STORAGE_BASE_URL ||
  'https://storage.googleapis.com/kids-place.appspot.com'
).replace(/\/+$/, '');

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

function requireApiBase() {
  if (!API_BASE) {
    throw new Error(
      'VITE_API_BASE_URL is not configured. Set it to your kidsstories_backend API root (e.g. /stories-api or http://kidsstories.local/api).',
    );
  }
  return API_BASE;
}

/** Strip trailing commas so slightly-invalid JSON from storage still parses. */
function cleanJsonText(raw) {
  return String(raw || '').replace(/,\s*([}\]])/g, '$1');
}

function parseJsonPayload(data) {
  if (Array.isArray(data) || (data !== null && typeof data === 'object')) return data;
  if (typeof data === 'string') return JSON.parse(cleanJsonText(data));
  throw new Error('Unexpected response format — neither object nor string.');
}

/** Build absolute GCS URL from a relative kidsplace-stories path (for Laravel url= param only). */
function toAbsoluteStorageUrl(src) {
  if (!src || typeof src !== 'string') return '';
  const trimmed = src.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  let rel = trimmed.replace(/^\/+/, '');
  if (!/^kidsplace-stories\//i.test(rel)) {
    rel = `kidsplace-stories/${rel}`;
  }
  return `${STORAGE_BASE_URL}/${rel}`;
}

/**
 * Proxy an image/audio URL through Laravel so the browser never hits Firebase Storage.
 */
export function resolveStoryAssetUrl(src) {
  const absolute = toAbsoluteStorageUrl(src);
  if (!absolute) return '';
  if (!API_BASE) return absolute;
  return `${API_BASE}/stories/asset?url=${encodeURIComponent(absolute)}`;
}

/**
 * Fetch the character/story catalog via Laravel (server fetches catalog_v2.json).
 * Returns [{ title, image, items: [...] }].
 */
export async function fetchStoriesCatalog() {
  const api = requireApiBase();
  const { data } = await axios.get(`${api}/stories/catalog-v2`, {
    timeout: 30000,
    headers: { Accept: 'application/json, text/plain, */*' },
  });
  const rawData = parseJsonPayload(data);
  const list = Array.isArray(rawData) ? rawData : parseJsonPayload(rawData);
  return Array.isArray(list) ? list.filter((r) => r && typeof r === 'object') : [];
}

/**
 * Fetch storyplay JSON via Laravel (server fetches Firebase; browser only talks to API).
 * `url` is the catalog item's original storage URL — passed as a query param, not fetched directly.
 */
export async function fetchStoryplayJson(url) {
  if (!url || typeof url !== 'string') throw new Error('fetchStoryplayJson: missing url.');
  const api = requireApiBase();
  const { data } = await axios.get(`${api}/stories/storyplay`, {
    params: { url: url.trim() },
    timeout: 120000,
    headers: { Accept: 'application/json, text/plain, */*' },
  });
  return parseJsonPayload(data);
}

export function normalizeStoryScene(scene) {
  let text = scene?.text;
  if (typeof text === 'string') text = [text];
  if (!Array.isArray(text)) text = [''];

  let audio = scene?.audio;
  if (typeof audio === 'string') audio = [audio];
  if (!Array.isArray(audio)) audio = [];
  audio = audio.map((a) => resolveStoryAssetUrl(a)).filter(Boolean);

  const img = scene?.image;
  const image =
    img && typeof img === 'object'
      ? { src: resolveStoryAssetUrl(img.src || ''), style: img.style || 'in-story' }
      : { src: '', style: 'in-story' };

  return { text, image, audio };
}

export function splitScenes(scenes) {
  if (!Array.isArray(scenes)) return [];
  let pageIndex = 0;
  return scenes.flatMap((scene, sceneIndex) =>
    scene.text.map((text, i) => ({
      text: [text],
      image: scene.image,
      audio: scene.audio[i] !== undefined ? [scene.audio[i]] : [],
      sceneIndex,
      pageIndex: pageIndex++,
    })),
  );
}
